import type { PlatformTurn } from '@liks/backend-svc-api/esm/models/index';
import {
  isPlatformMessageValue,
  isPlatformTurnValue,
  isRecord,
} from './platformChatServiceGuards';

export function textFromStreamPayload(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  if (!isRecord(value)) {
    return null;
  }

  const delta = value.delta;
  if (isRecord(delta) && typeof delta.content === 'string') {
    return delta.content;
  }
  if (typeof value.content === 'string') {
    return value.content;
  }
  if (typeof value.token === 'string') {
    return value.token;
  }
  if (typeof value.text === 'string') {
    return value.text;
  }

  return null;
}

export function mergeStreamedTurn(
  current: PlatformTurn,
  incoming: PlatformTurn,
): PlatformTurn {
  return {
    userMessage: incoming.userMessage ?? current.userMessage,
    assistantMessage: incoming.assistantMessage ?? current.assistantMessage,
  };
}

export function appendStreamedText(current: string, next: string): string {
  return next.startsWith(current) ? next : `${current}${next}`;
}

export function applyStreamedEvent(
  payload: string,
  state: {
    turn: PlatformTurn;
    emitChunk: (value: string) => void;
  },
): { done: boolean; turn: PlatformTurn } {
  const trimmedPayload = payload.trim();
  if (trimmedPayload === '') {
    return { done: false, turn: state.turn };
  }
  if (trimmedPayload === '[DONE]') {
    return { done: true, turn: state.turn };
  }

  let turn = state.turn;

  try {
    const parsed: unknown = JSON.parse(trimmedPayload);

    if (isPlatformTurnValue(parsed)) {
      turn = mergeStreamedTurn(turn, parsed);
      const assistantContent = parsed.assistantMessage?.content;
      if (
        typeof assistantContent === 'string' &&
        assistantContent.length > 0 &&
        parsed.assistantMessage?.role === 'ASSISTANT'
      ) {
        state.emitChunk(assistantContent);
      }
      return { done: false, turn };
    }

    if (isPlatformMessageValue(parsed)) {
      if (parsed.role === 'USER') {
        turn = { ...turn, userMessage: parsed };
        return { done: false, turn };
      }

      turn = { ...turn, assistantMessage: parsed };
      if (typeof parsed.content === 'string' && parsed.content.length > 0) {
        state.emitChunk(parsed.content);
      }
      return { done: false, turn };
    }

    const chunk = textFromStreamPayload(parsed);
    if (chunk != null) {
      state.emitChunk(chunk);
    }
  } catch {
    state.emitChunk(trimmedPayload);
  }

  return { done: false, turn };
}
